import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Editor from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useMemo, useCallback, useEffect, useContext } from 'react';
import { Button, ModalWindow, Select, Spinner, Table, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { StudiesContext } from '../../../contexts';
import { appendPreviewLimitIfMissing } from '../../../utils/sql_preview.utils';
import { isLegacyInfrastructure } from '../../constants/data_view.constants';
import { toSnakeCase } from '../../utils';
export const AddEditDimensionModal = ({ isOpen, onClose, onSave, dataView, isLoading = false, isEditMode = false, isReadOnly = false, }) => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const { studies } = useContext(StudiesContext);
    const webClient = useApiClient(ApiClientService.WEB);
    const momClient = useApiClient(ApiClientService.MOM);
    const usesNewInfrastructure = !isLegacyInfrastructure(dataView.data?.data_source);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const previewContainerRef = useRef(null);
    const [sqlQuery, setSqlQuery] = useState('');
    const [queryResult, setQueryResult] = useState([]);
    const [isRunningQuery, setIsRunningQuery] = useState(false);
    const [hasRunQuery, setHasRunQuery] = useState(false);
    const hasAutoRunRef = useRef(false);
    // Local state for study and environment selection (for preview only)
    const [selectedStudy, setSelectedStudy] = useState(undefined);
    const [selectedEnvironment, setSelectedEnvironment] = useState(undefined);
    const studyOptions = useMemo(() => studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    })), [studies]);
    const fetchEnvironments = useCallback(async () => {
        if (!selectedStudy?.value) {
            return [];
        }
        try {
            const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${selectedStudy.value}/environments`, {
                params: {
                    clusterTypes: 'dev',
                    withCortexAccess: true,
                },
            });
            return response.data.filter((env) => !env.deployment.isProd);
        }
        catch (error) {
            return [];
        }
    }, [selectedStudy?.value, entityId, momClient]);
    const { data: environments = [], isLoading: isLoadingEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudy?.value, 'environments'],
        queryFn: fetchEnvironments,
        enabled: !!entityId && !!selectedStudy?.value,
        refetchOnWindowFocus: false,
    });
    const environmentOptions = useMemo(() => environments.map((env) => ({
        label: env.name,
        value: env.cortexId || env.id.toString(),
    })), [environments]);
    // Reset environment when study changes
    const handleStudyChange = useCallback((option) => {
        setSelectedStudy(option);
        setSelectedEnvironment(undefined);
    }, []);
    const handleEnvironmentChange = useCallback((option) => {
        setSelectedEnvironment(option);
    }, []);
    // Load SQL query when opening in edit mode
    useEffect(() => {
        if (isOpen) {
            if (isEditMode && dataView.data?.sql) {
                setSqlQuery(dataView.data.sql);
                hasAutoRunRef.current = false;
            }
            else if (!isEditMode) {
                setSqlQuery('');
                hasAutoRunRef.current = false;
            }
            setQueryResult([]);
            setHasRunQuery(false);
            // Reset study/environment selection when modal opens
            setSelectedStudy(undefined);
            setSelectedEnvironment(undefined);
        }
        else {
            hasAutoRunRef.current = false;
        }
    }, [isOpen, isEditMode, dataView.data?.sql]);
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
    };
    const handleSQLChange = useCallback((value) => {
        setSqlQuery(value || '');
    }, []);
    const handleValidateOrRunQuery = useCallback(async (explain) => {
        if (!sqlQuery.trim() || !selectedStudy?.value || !selectedEnvironment?.value) {
            return;
        }
        setIsRunningQuery(true);
        if (!explain) {
            setQueryResult([]);
        }
        try {
            const trimmedSql = appendPreviewLimitIfMissing(sqlQuery);
            const response = await webClient.post(`/api/data_transfer/configs/query`, {
                sql: trimmedSql,
                explain,
                environmentId: selectedEnvironment.value,
                studyId: selectedStudy.value,
                useAuthGateway: usesNewInfrastructure,
            });
            if (explain) {
                toast.addNotification({
                    type: 'success',
                    title: translate('SQL query validated successfully'),
                });
            }
            else {
                setQueryResult(response.data || []);
                setHasRunQuery(true);
            }
        }
        catch (error) {
            const axiosError = error;
            toast.addNotification({
                type: 'error',
                title: explain ? translate('SQL query validation failed') : translate('Error running query'),
                subtitle: axiosError.response?.data?.message || translate('Failed to execute SQL query'),
            });
            if (!explain) {
                setQueryResult([]);
            }
        }
        finally {
            setIsRunningQuery(false);
        }
    }, [sqlQuery, selectedStudy?.value, selectedEnvironment?.value, webClient, usesNewInfrastructure, toast, translate]);
    useEffect(() => {
        const container = previewContainerRef.current;
        if (!container) {
            return;
        }
        const tableRoot = container.querySelector('[data-testid="nucleus-table--container"]');
        if (!tableRoot) {
            return;
        }
        tableRoot.style.height = '100%';
        tableRoot.style.minHeight = '0';
        const tableWrapper = tableRoot.querySelector(':scope > .nucleus-relative');
        if (tableWrapper) {
            tableWrapper.style.flex = '1';
            tableWrapper.style.minHeight = '0';
            tableWrapper.style.display = 'flex';
            tableWrapper.style.flexDirection = 'column';
        }
        const scrollWrapper = container.querySelector('.nucleus-overflow-x-auto');
        if (scrollWrapper) {
            scrollWrapper.style.overflow = 'auto';
            scrollWrapper.style.minHeight = '0';
        }
    }, [queryResult, isRunningQuery]);
    const columns = useMemo(() => {
        return queryResult.length > 0
            ? Object.keys(queryResult[0]).map((column) => ({
                header: column,
                accessorKey: column,
            }))
            : [];
    }, [queryResult]);
    const handleCreate = useCallback(() => {
        if (queryResult.length === 0 || columns.length === 0) {
            toast.addNotification({
                type: 'error',
                title: translate('Please run a query first to create dimensions'),
            });
            return;
        }
        // Extract dimensions from query results. Each column becomes a dimension.
        // In edit mode, preserve existing Data settings for matching columns (SSP-4237).
        const existingDimensions = dataView.data?.dimensions;
        const dimensions = columns.map((col) => {
            const columnName = col.header;
            const dimensionKey = toSnakeCase(columnName.toLowerCase().replace(/\s+/g, '_'));
            const existing = isEditMode ? existingDimensions?.[dimensionKey] : undefined;
            if (existing) {
                return {
                    name: dimensionKey,
                    type: existing.type,
                    sql: columnName,
                    title: existing.title || columnName,
                    description: existing.description,
                };
            }
            // New column (or create mode): infer type from the first preview row.
            const firstValue = queryResult[0]?.[columnName];
            let inferredType = 'string';
            if (typeof firstValue === 'number') {
                inferredType = 'number';
            }
            else if (typeof firstValue === 'boolean') {
                inferredType = 'boolean';
            }
            return {
                name: dimensionKey,
                type: inferredType,
                sql: columnName,
                title: columnName,
            };
        });
        onSave(dimensions, sqlQuery);
    }, [queryResult, columns, onSave, sqlQuery, toast, translate, isEditMode, dataView.data?.dimensions]);
    const createButton = isReadOnly
        ? undefined
        : {
            label: translate(isEditMode ? 'Save' : 'Create'),
            onClick: handleCreate,
            disabled: isLoading || queryResult.length === 0 || columns.length === 0,
        };
    const closeButton = {
        label: translate('Close'),
        onClick: onClose,
    };
    const cancelButton = {
        label: translate('Cancel'),
        onClick: onClose,
    };
    const getModalTitle = () => {
        if (isReadOnly) {
            return translate('View dimensions');
        }
        return translate(isEditMode ? 'Edit dimensions' : 'Create dimensions');
    };
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: getModalTitle(), isOpen: true, closeWindow: onClose, footerPrimaryActionButton: createButton, footerSecondaryActionButtons: [isReadOnly ? closeButton : cancelButton], width: 'full', children: _jsxs("div", { className: 'grid grid-cols-2 gap-4 size-full min-h-[500px] overflow-hidden', children: [_jsxs("div", { className: 'space-y-4 size-full flex flex-col min-w-0', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsx("div", { children: translate('Editor') }), _jsxs("div", { className: 'flex items-center gap-4', children: [_jsx(Select, { placeholder: translate('Select a study'), options: studyOptions, value: selectedStudy, onChange: handleStudyChange, width: 'full', searchableOptions: true }), _jsx(Select, { placeholder: translate('Select an environment'), options: environmentOptions, value: selectedEnvironment, onChange: handleEnvironmentChange, width: 'full', disabled: !selectedStudy?.value || isLoadingEnvironments, searchableOptions: true }), _jsx(Button, { label: translate('Validate'), onClick: () => handleValidateOrRunQuery(true), disabled: !sqlQuery.trim() || isRunningQuery || !selectedStudy?.value || !selectedEnvironment?.value, variant: 'secondary' }), _jsx(Button, { label: translate('Run'), onClick: () => handleValidateOrRunQuery(false), disabled: !sqlQuery.trim() || isRunningQuery || !selectedStudy?.value || !selectedEnvironment?.value, variant: 'primary' })] })] }), _jsx("div", { className: 'border rounded-lg flex-1 py-4', children: _jsx(Editor, { height: '100%', defaultLanguage: 'sql', value: sqlQuery, onChange: isReadOnly ? undefined : handleSQLChange, onMount: handleEditorDidMount, options: {
                                    minimap: { enabled: false },
                                    fontSize: 14,
                                    formatOnType: true,
                                    lineNumbers: 'on',
                                    automaticLayout: true,
                                    readOnly: isReadOnly,
                                    domReadOnly: isReadOnly,
                                } }) })] }), _jsxs("div", { className: 'border-l px-4 space-y-4 min-w-0 overflow-hidden flex flex-col', children: [_jsx("div", { className: 'text-md font-semibold', children: translate('Data preview') }), _jsx("div", { ref: previewContainerRef, className: 'flex-1 min-h-0 overflow-hidden', children: isRunningQuery ? (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) })) : queryResult.length > 0 ? (_jsx(Table, { columns: columns, data: queryResult, hasPagination: true, paginationNavigationScreenReaderLabel: translate('Data preview pagination'), initialPageSize: 5 })) : (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx("div", { className: 'text-center text-text-secondary', children: hasRunQuery
                                        ? translate('No results found.')
                                        : translate('Select a study and environment, then click Run to preview data.') }) })) })] })] }) }));
};
